import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Briefcase, Home, Wrench } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';

/**
 * One-time onboarding dialog shown to new Google-OAuth users so they can
 * declare whether they are a Manager, Owner or Cleaner/Worker. Google login
 * bypasses the regular register form, so without this step everyone would
 * default to "cleaner" and be unable to create properties.
 */
const ROLE_OPTIONS = [
  {
    value: 'property_manager',
    title: 'Property Manager',
    description:
      'I manage rentals, coordinate cleanings & maintenance, and want to invite staff.',
    icon: Briefcase,
  },
  {
    value: 'owner',
    title: 'Property Owner',
    description:
      'I own the property and want visibility over bookings, expenses and reports.',
    icon: Home,
  },
  {
    value: 'cleaner',
    title: 'Cleaner / Worker',
    description:
      'I was invited to help clean, maintain or repair. My manager will assign my tasks.',
    icon: Wrench,
  },
];

export default function RolePickerDialog({ open }) {
  const { refreshUser } = useAuth();
  const [selected, setSelected] = useState(null);
  const [saving, setSaving] = useState(false);

  const handleContinue = async () => {
    if (!selected) return;
    setSaving(true);
    try {
      await api.post('/auth/select-role', { role: selected });
      await refreshUser();
      toast.success('Welcome to PROP VEIW!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not save your role. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open}>
      {/* No onOpenChange — this dialog is intentionally not dismissable
          until the user picks a role. */}
      <DialogContent
        className="max-w-lg [&>button[aria-label='Close']]:hidden [&_button.absolute.right-4.top-4]:hidden"
        data-testid="role-picker-dialog"
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle className="text-2xl">One quick question</DialogTitle>
          <DialogDescription>
            How will you be using PROP VEIW? You can change this later from your
            profile.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 mt-2">
          {ROLE_OPTIONS.map((opt) => {
            const Icon = opt.icon;
            const isActive = selected === opt.value;
            return (
              <button
                key={opt.value}
                type="button"
                data-testid={`role-option-${opt.value}`}
                onClick={() => setSelected(opt.value)}
                className={`w-full text-left flex gap-4 p-4 rounded-lg border-2 transition-all ${
                  isActive
                    ? 'border-[#C9A44C] bg-[#FDF7E7]'
                    : 'border-slate-200 hover:border-slate-300 bg-white'
                }`}
              >
                <div
                  className={`flex-shrink-0 h-10 w-10 rounded-md flex items-center justify-center ${
                    isActive ? 'bg-[#C9A44C] text-white' : 'bg-slate-100 text-slate-600'
                  }`}
                >
                  <Icon className="h-5 w-5" />
                </div>
                <div className="flex-1">
                  <div className="font-semibold text-slate-900">{opt.title}</div>
                  <div className="text-sm text-slate-500 mt-1">{opt.description}</div>
                </div>
              </button>
            );
          })}
        </div>

        <div className="mt-4">
          <Button
            className="w-full bg-[#0B1E3F] hover:bg-[#0B1E3F]/90 text-white"
            disabled={!selected || saving}
            onClick={handleContinue}
            data-testid="role-picker-continue-btn"
          >
            {saving ? 'Saving…' : 'Continue'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
